import { useCallback, useEffect, useRef, useState } from 'react';

import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { StoreModel } from '~/core/models';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedStore, updateSelectedStore } from '~/redux/slices/store';
import { useDrawingShapeContext } from '~contexts';

export const useStoreSelection = () => {
  const [storeCandidate, setStoreCandidate] = useState<StoreModel | undefined>(
    undefined
  );
  const [
    isChangeStoreConfirmationModalOpen,
    setIsChangeStoreConfirmationModalOpen,
  ] = useState(false);

  const dispatch = useAppDispatch();
  const selectedStore = useAppSelector(selectSelectedStore);
  const { replaceProductsWithEquivalents } = useEquivalentProductMatching();

  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();

  const handleStoreSelection = useCallback(
    (store: StoreModel) => () => {
      if (store.companyId === selectedStore?.companyId) {
        return;
      }

      if (areThereShapesInTheDrawingArea && selectedStore) {
        setStoreCandidate(store);
        setIsChangeStoreConfirmationModalOpen(true);
      } else {
        // Reconcile ancillary product ids against the new store's catalog before
        // committing, so foreign ids are never persisted on this path. Best-effort:
        // if the reconcile fetch fails the commit still runs.
        void (async () => {
          try {
            await replaceProductsWithEquivalents({ storeId: store.companyId });
          } catch {
            /* best-effort */
          }
          // Pre-set the dedupe ref before dispatching the commit so the
          // auto-commit watcher (below) skips the redundant second reconcile
          // when selectedStore changes. Auto-commit paths (e.g. default/online-
          // retailer selection) never reach this branch, so the ref is not
          // pre-set there and the watcher still fires once for those.
          lastReconciledStoreIdRef.current = store.companyId;
          dispatch(updateSelectedStore(store));
        })();
      }
    },
    [
      selectedStore,
      areThereShapesInTheDrawingArea,
      dispatch,
      replaceProductsWithEquivalents,
    ]
  );

  const confirmStoreChange = useCallback(async () => {
    if (storeCandidate) {
      try {
        await replaceProductsWithEquivalents({
          storeId: storeCandidate.companyId,
        });
      } catch {
        /* best-effort */
      }
      // Pre-set the dedupe ref before committing so the auto-commit watcher
      // skips the redundant second reconcile when the dispatch lands.
      lastReconciledStoreIdRef.current = storeCandidate.companyId;
    }

    dispatch(updateSelectedStore(storeCandidate));
    setIsChangeStoreConfirmationModalOpen(false);
    setStoreCandidate(undefined);
  }, [storeCandidate, replaceProductsWithEquivalents, dispatch]);

  const cancelStoreChange = useCallback(() => {
    setIsChangeStoreConfirmationModalOpen(false);
    setStoreCandidate(undefined);
  }, []);

  // Auto-commit watcher: store selection can change without a user click (e.g.
  // default/online-retailer auto-selection). Reconcile ancillaries to the
  // committed store on that change so foreign ids cannot survive an auto-default
  // store switch. Idempotent/best-effort; the ref prevents re-firing for the
  // same committed store (and avoids double work when a user-initiated commit
  // already reconciled the same target).
  const lastReconciledStoreIdRef = useRef<number | undefined>(
    selectedStore?.companyId
  );

  useEffect(() => {
    const committedId = selectedStore?.companyId;

    if (!committedId || committedId === lastReconciledStoreIdRef.current) {
      return;
    }
    lastReconciledStoreIdRef.current = committedId;

    // Only reconcile when there is a design to reconcile (no shapes => no
    // ancillary ids to fix); matches the "nothing to reconcile" reasoning in Task 2.
    if (!areThereShapesInTheDrawingArea) {
      return;
    }
    void replaceProductsWithEquivalents({ storeId: committedId }).catch(() => {
      /* best-effort */
    });
  }, [
    selectedStore?.companyId,
    areThereShapesInTheDrawingArea,
    replaceProductsWithEquivalents,
  ]);

  return {
    storeCandidate,
    handleStoreSelection,
    confirmStoreChange,
    cancelStoreChange,
    isChangeStoreConfirmationModalOpen,
    lastReconciledStoreIdRef,
  };
};
